import { useTranslate } from "@refinedev/core";
import React, { useRef, useEffect } from "react";
import { ConfigProvider, Input, InputProps, InputRef, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useAntTheme } from "hooks/useAntTheme";
import styled from "styled-components";

interface SearchInputProps {
  onSearch: (searchText: string) => void;
  value: string;
  placeholder?: string;
  prefixStyle?: React.CSSProperties;
}

const SearchInput: React.FC<SearchInputProps & InputProps> = ({
  onSearch,
  value,
  placeholder,
  prefixStyle,
  ...rest
}) => {
  const t = useTranslate();
  const searchInput = useRef<InputRef>(null);
  const { theme } = useAntTheme();

  const handleChange = () => {
    const value = searchInput.current?.input.value ?? "";
    onSearch(value);
  };

  useEffect(() => {
    const handleKeyPressShortcut = (e: KeyboardEvent) => {
      if (e.key === "/" || (e.ctrlKey && e.key === "f")) {
        e.preventDefault();
        if (searchInput.current) {
          searchInput.current.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyPressShortcut);
    return () => {
      document.removeEventListener("keydown", handleKeyPressShortcut);
    };
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: theme.colorBgElevated,
          colorText: theme.colorTextDescription,
        },
        components: {
          Input: {
            addonBg: theme.colorBgElevated,
            controlHeightLG: 34,
          },
        },
      }}
    >
      <Space.Compact
        style={{
          width: "100%",
          border: "1.5px solid",
          borderRadius: theme.borderRadiusLG + 2,
          borderColor: theme.colorLightGrey,
          backgroundColor: theme.colorBgBase,
        }}
      >
        <SearchOutlined
          style={{
            padding: "0 0 0 11px",
            color: theme.colorTextDescription,
            fontSize: 16,
            ...prefixStyle,
          }}
        />
        <StyledInput
          ref={searchInput}
          size="large"
          variant="borderless"
          placeholder={
            placeholder ||
            t("projects.components.SearchInput.searchExperiences")
          }
          value={value}
          onChange={handleChange}
          allowClear
          onClear={() => {
            onSearch("");
          }}
          {...rest}
        />
      </Space.Compact>
    </ConfigProvider>
  );
};

const StyledInput = styled(Input)`
  input::placeholder {
    color: ${({ theme }) => theme.colorTextDescription};
    font-weight: 200;
  }
`;

export default SearchInput;
